import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import {
  PrivateOfficeContract,
  Pagination,
} from "@/services/private-office-contracts-service/types";
import { ListContextProps } from "./use-list.types";
import { createToast } from "vercel-toast";
import { formatInTimeZone } from "date-fns-tz";
import { isBefore, getYear } from "date-fns";
import { UPDATE_PAGINATION } from "@/constants/pagination";
import { privateOfficeContractsService } from "@/services/private-office-contracts-service";
import { useSearchParams } from "react-router-dom";
import { parseContractHistory } from "@/features/private-room/utils/parseContractHistory";

const ListContext = createContext<ListContextProps | undefined>(undefined);

export const ListProvider = ({ children }: { children: React.ReactNode }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const poContractIdParam = searchParams.get("po_contract_id");
  const poContractIdListParam = searchParams.get("po_contract_id_list");

  const [data, setData] = useState<PrivateOfficeContract[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    total: 0,
    page: 1,
    take: 10,
  });
  const [activePage, setActivePage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState(() => ({
    poContractId: poContractIdParam || "",
    poContractIdList: poContractIdListParam
      ? poContractIdListParam
          .split(",")
          .filter(Boolean)
          .filter((id) => /^\d+$/.test(id.trim()))
      : [],
    space: "",
    company: "",
    startDate: "",
    endDate: "",
    status: "",
  }));
  const [poContractToDetail, setPOContractToDetail] = useState<number | null>(
    null,
  );

  const updateFilters = (newFilters: Partial<ListContextProps["filters"]>) => {
    if (
      newFilters.startDate &&
      newFilters.endDate &&
      getYear(new Date(newFilters.endDate)) >= 2020 && // Preventing validation while inputting date
      isBefore(
        formatInTimeZone(newFilters.endDate, "UTC", "yyyy-MM-dd"),
        formatInTimeZone(newFilters.startDate, "UTC", "yyyy-MM-dd"),
      )
    ) {
      createToast("Data de início deve ser anterior a data final", {
        type: "error",
        timeout: 2000,
      });
      return;
    }

    if (newFilters.poContractId) {
      searchParams.delete("po_contract_id_list");
      setSearchParams(searchParams, { replace: true });
      newFilters.poContractIdList = [];
    }

    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  const updateActivePage = (page: number) => {
    setActivePage(page);
  };

  const handleChangePage = async (page: number) => {
    updateActivePage(page);
    fetchData(page);
  };

  const changeActivePage = (update: UPDATE_PAGINATION) => {
    const operation =
      update === UPDATE_PAGINATION.INCREMENT ? activePage + 1 : activePage - 1;
    updateActivePage(operation);
    fetchData(operation);
  };

  const decreasePage = () => {
    changeActivePage(UPDATE_PAGINATION.DECREMENT);
  };

  const increasePage = () => {
    changeActivePage(UPDATE_PAGINATION.INCREMENT);
  };

  const clearFilters = () => {
    setFilters({
      space: "",
      company: "",
      poContractId: "",
      poContractIdList: [],
      startDate: "",
      endDate: "",
      status: "",
    });

    searchParams.delete("po_contract_id");
    searchParams.delete("po_contract_id_list");
    setSearchParams(searchParams, { replace: true });
  };

  const fetchContractHistory = useCallback(async (contractId: number) => {
    const data =
      await privateOfficeContractsService.getContractHistory(contractId);

    return parseContractHistory(data);
  }, []);

  const fetchDetailsData = async (id: number) => {
    const data =
      await privateOfficeContractsService.getPrivateOfficeContractById(id);
    return data;
  };

  const fetchData = async (page: number = 1) => {
    setIsLoading(true);
    try {
      const { contracts, pagination } =
        await privateOfficeContractsService.listPrivateOfficeContracts({
          ...filters,
          page: page,
          orderBy: "-updatedAt",
        });

      setData(contracts);
      setPagination(pagination);

      const countPages = Math.ceil(pagination.total / pagination.take);

      if (countPages === 1 && page > 1) {
        handleChangePage(1);
        setActivePage(1);
      } else {
        setActivePage(page);
      }
      setTotalPages(countPages);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportData = async () => {
    const { contracts } =
      await privateOfficeContractsService.listPrivateOfficeContracts({
        ...filters,
        page: 1,
        take: 10000,
        allData: true,
        orderBy: "-updatedAt",
      });

    return contracts;
  };

  useEffect(() => {
    fetchData();
  }, [filters]);

  return (
    <ListContext.Provider
      value={{
        data,
        pagination,
        isLoading,
        filters,
        updateFilters,
        clearFilters,
        activePage,
        handleChangePage,
        decreasePage,
        increasePage,
        totalPages,
        poContractToDetail,
        setPOContractToDetail,
        fetchDetailsData,
        handleExportData,
        fetchContractHistory,
      }}
    >
      {children}
    </ListContext.Provider>
  );
};

export const useList = () => {
  const context = useContext(ListContext);
  if (!context) {
    throw new Error("useList must be used within a ListProvider");
  }
  return context;
};
